import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/shopify-plus";
const TITLE = "Shopify Plus: Leistungen, Kosten und wann sich der Wechsel lohnt";
const DESCRIPTION =
  "Was Shopify Plus gegenüber den Standardtarifen bietet, welche Kosten anfallen, wann ein Wechsel sinnvoll ist und wie die Umsetzung mit einem Freelancer abläuft.";

const faqs = [
  {
    q: "Was ist Shopify Plus?",
    a: "Shopify Plus ist der Enterprise-Tarif von Shopify. Er richtet sich an Shops mit hohem Bestellvolumen oder besonderen Anforderungen und bietet unter anderem mehr Kontrolle über den Checkout, mehrere Shops unter einer Organisation, höhere API-Limits und Automatisierungen wie Shopify Flow.",
  },
  {
    q: "Was kostet Shopify Plus?",
    a: "Shopify nennt für Plus einen Einstieg im vierstelligen Bereich pro Monat, abhängig von Umsatz und Vertragsmodell. Den verbindlichen Preis nennt Shopify im Angebot. Dazu kommen Umsetzungskosten für Theme, Migration und Anpassungen.",
  },
  {
    q: "Wann lohnt sich der Wechsel auf Shopify Plus?",
    a: "Wenn Sie tatsächlich an Grenzen stoßen: Checkout-Anpassungen, die im Standardtarif nicht möglich sind, mehrere Länder- oder Markenshops, hohe Lastspitzen, B2B-Preislogik oder umfangreiche Automatisierung. Wenn keiner dieser Punkte zutrifft, bringt der Wechsel meist keinen zusätzlichen Umsatz.",
  },
  {
    q: "Was ist beim Checkout unter Plus möglich?",
    a: "Plus erlaubt weitergehende Eingriffe in Checkout und Kassenlogik, etwa über Checkout Extensibility und Shopify Functions, zum Beispiel eigene Rabatt-, Versand- oder Zahlungsregeln. Der Umfang hängt von der aktuellen Shopify-Plattformversion ab und sollte vor dem Wechsel geprüft werden.",
  },
  {
    q: "Kann ein Freelancer Shopify-Plus-Projekte umsetzen?",
    a: "Ja, für Theme-Entwicklung, Migration, Checkout-Anpassungen, Automatisierungen und Conversion-Arbeit. Bei sehr großen Programmen mit vielen parallelen Gewerken ist ein Team sinnvoll.",
  },
  {
    q: "Wie läuft ein Wechsel technisch ab?",
    a: "Bestandsaufnahme des aktuellen Shops, Prüfung von Apps und Anpassungen, Aufbau in einer Kopie oder einem Testshop, Datenübernahme, Weiterleitungen, Test und Livegang. Der Aufwand hängt vor allem von der Zahl individueller Anpassungen ab.",
  },
];

export const Route = createFileRoute("/shopify-plus")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Orientierung · Shopify Plus"
      title="Shopify Plus: was es bringt und wann es sich lohnt"
      intro="Shopify Plus ist kein besserer Shop, sondern ein größerer Rahmen. Diese Übersicht zeigt, welche Möglichkeiten dazukommen, welche Kosten realistisch sind und wie ein Wechsel ohne Umsatzeinbruch abläuft."
      source="shopify-plus"
      sections={[
        {
          h: "Was Plus zusätzlich möglich macht",
          p: [],
          list: [
            "Weitergehende Eingriffe in Checkout und Kassenlogik",
            "Mehrere Shops für Länder, Sprachen oder Marken unter einer Organisation",
            "B2B-Funktionen mit Firmenkonten und eigenen Preislisten",
            "Automatisierung wiederkehrender Abläufe über Shopify Flow",
            "Höhere API-Limits und mehr Spielraum bei Lastspitzen",
          ],
        },
        {
          h: "Kosten realistisch einschätzen",
          p: [
            "Die Lizenz liegt deutlich über den Standardtarifen und wird von Shopify individuell angeboten. Entscheidend ist aber selten die Lizenz allein, sondern der Aufwand für Theme, Migration, Apps und Anpassungen.",
            "Bei mir: 110 € netto pro Stunde für Anpassungen und Entwicklung, Festpreis nach Umfang für Shopaufbau und Migration, Conversion-Optimierung ab 3.990 € netto.",
          ],
        },
        {
          h: "Wann ein Wechsel sinnvoll ist",
          p: [],
          list: [
            "Der Checkout soll Regeln abbilden, die der aktuelle Tarif nicht erlaubt",
            "Mehrere Märkte oder Marken brauchen eigene Shops mit gemeinsamer Verwaltung",
            "B2B und B2C laufen parallel mit unterschiedlichen Preisen",
            "Manuelle Abläufe binden regelmäßig Arbeitszeit",
          ],
        },
        {
          h: "Wann der Standardtarif genügt",
          p: [
            "Viele Shops nutzen ihre bestehenden Möglichkeiten noch nicht aus. Wenn Produktseiten, Warenkorb, mobile Nutzung und Ladezeit Potenzial haben, bringt Arbeit an diesen Punkten in der Regel mehr als ein Tarifwechsel.",
            "Ich sage Ihnen vorher, wenn ein Wechsel aus meiner Sicht nicht nötig ist.",
          ],
        },
        {
          h: "Ablauf einer Umstellung",
          p: [],
          list: [
            "Bestandsaufnahme: Theme, Apps, Anpassungen, Datenstruktur",
            "Aufbau in einer Theme-Kopie oder einem Testshop",
            "Datenübernahme, Weiterleitungen und Prüfung der Suchmaschinen-relevanten URLs",
            "Test von Bestellung, Zahlung, Versand und Steuern",
            "Livegang und Nachbetreuung nach Aufwand",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-entwickler", label: "Shopify Entwickler buchen" },
        { to: "/ratgeber/shopify-migration", label: "Shopify Migration" },
        { to: "/shopify-conversion-optimierung", label: "Conversion-Optimierung" },
      ]}
    />
  );
}
